import React, { useCallback } from 'react';

import styled from '@emotion/styled';
import { Button as MuiButton } from '@mui/material';
import { Blockchain } from '@repo/prsn-blockchain';
import { useTranslations } from 'next-intl';
import { useBoolean } from 'usehooks-ts';

import { useBridgeManager } from '@web/features/staking/BridgeManager';
import {
  useBlockchain,
  useDepositsOrThrow,
  useUnstakedPersonas,
} from '@web/features/staking/hooks';
import { useCurrentCompletedUser } from '@web/features/user/service';
import { showSnackbar } from '@web/ui/components';

import { StakeModal } from '../../modals/StakeModal';
import { UnstakeModal } from '../../modals/UnstakeModal';

const StakingActionsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing('0250')};
  color: ${({ theme }) => theme.color.background};

  padding-top: ${({ theme }) => theme.spacing('1000')};
`;

const StyledMuiButton = styled(MuiButton)`
  height: ${({ theme }) => theme.size[2500]};
  padding: ${({ theme }) => theme.spacing('1000')};
  padding-left: ${({ theme }) => theme.spacing('2000')};
  display: flex;
  justify-content: flex-start;
  align-items: center;

  color: ${({ theme }) => theme.color.greenFixed};

  font: ${({ theme }) => theme.typography.body.regular};
  font-size: ${({ theme }) => theme.typescale.size['1']};
  border-radius: ${({ theme }) => theme.shape.corner['0500']};
  text-shadow: 0px 0px 4px ${({ theme }) => theme.color.greenFixed};
  transition: padding-left 150ms ease;

  &:after {
    content: '';
    position: absolute;
    background: ${({ theme }) => theme.color.greenFixed};
    opacity: ${({ theme }) => theme.opacity.semiTransparent};
    pointer-events: none;
    inset: 0;
    border-radius: ${({ theme }) => theme.shape.corner['0500']};
  }

  &:hover {
    padding-left: ${({ theme }) => theme.spacing('2500')};
    transition: padding-left 150ms ease;
    background: none;

    &:after {
      opacity: ${({ theme }) => theme.opacity.faded};
    }
  }

  &:active {
    border-radius: ${({ theme }) => theme.shape.corner[1000]};
    padding-left: ${({ theme }) => theme.spacing('2000')};

    &:after {
      opacity: ${({ theme }) => theme.opacity.dim};
      border-radius: ${({ theme }) => theme.shape.corner[1000]};
    }
  }
`;

export const StakingActions = () => {
  const t = useTranslations();
  const currentBlockchain = useBlockchain();
  const completedUser = useCurrentCompletedUser();

  const { tokenIds: stakedTokenIds } = useDepositsOrThrow();
  const { personas: personasToStake } = useUnstakedPersonas(currentBlockchain);
  const { createInstance } = useBridgeManager();

  const {
    value: isStakeModalOpen,
    setTrue: openStakeModal,
    setFalse: closeStakeModal,
  } = useBoolean(false);

  const {
    value: isUnstakeModalOpen,
    setTrue: openUnstakeModal,
    setFalse: closeUnstakeModal,
  } = useBoolean(false);

  const handleStakeAll = useCallback(() => {
    if (personasToStake.length === 0) {
      showSnackbar(t('STAKE_ALL_ALREADY_STAKED'));
      return;
    }

    if (currentBlockchain === Blockchain.BASE) {
      openStakeModal();
    } else {
      createInstance({
        tokenIds: personasToStake.map(({ tokenId }) => tokenId),
        source: currentBlockchain,
        stake: true,
      });
    }
  }, [personasToStake, currentBlockchain]);

  const handleUnstakeAll = useCallback(() => {
    if (stakedTokenIds.length === 0) {
      showSnackbar(t('UNSTAKE_ALL_ALREADY_UNSTAKED'));
      return;
    }

    openUnstakeModal();
  }, [stakedTokenIds]);

  return (
    <>
      <StakingActionsContainer>
        <StyledMuiButton onClick={handleStakeAll}>
          {`> ${t('STAKE_ALL_ACTION')}`}
        </StyledMuiButton>
        <StyledMuiButton onClick={handleUnstakeAll}>
          {`> ${t('UNSTAKE_ALL_ACTION')}`}
        </StyledMuiButton>
      </StakingActionsContainer>

      {completedUser && (
        <>
          <StakeModal
            personas={personasToStake}
            open={isStakeModalOpen}
            onClose={closeStakeModal}
          />

          <UnstakeModal
            personas={completedUser.user.personas.filter((p) =>
              stakedTokenIds.includes(p.tokenId),
            )}
            open={isUnstakeModalOpen}
            onClose={closeUnstakeModal}
          />
        </>
      )}
    </>
  );
};
